import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../../../components/ui/Modal';
import { Button } from '../../../../components/ui/Button';
import type { OrganizationPixKey } from '../../../../domain/pix/types';
import type { PixKeyType as DomainPixKeyType } from '../../../../domain/pix/types';
import { ChatPixSavedCard } from './ChatPixSavedCard';
import { ChatPixEditForm } from './ChatPixEditForm';
import { toChatInput, type ChatPixFormInput } from './chatPixInput';

export type { ChatPixFormInput };

interface ChatPixFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (input: ChatPixFormInput) => Promise<void>;
    savedKey: OrganizationPixKey | null;
    loading?: boolean;
    canManage: boolean;
    onSaveAndSend?: (input: ChatPixFormInput) => Promise<void>;
}

export const ChatPixForm: React.FC<ChatPixFormProps> = ({
    isOpen,
    onClose,
    onSubmit,
    savedKey,
    loading = false,
    canManage,
    onSaveAndSend,
}) => {
    const [editing, setEditing] = useState(false);
    const [pixKey, setPixKey] = useState('');
    const [type, setType] = useState<DomainPixKeyType>('cpf');
    const [name, setName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const resetFormFields = useCallback(() => {
        if (savedKey) {
            setPixKey(savedKey.pixKey);
            setType(savedKey.pixType);
            setName(savedKey.pixName);
            return;
        }
        setPixKey('');
        setType('cpf');
        setName('');
    }, [savedKey]);

    const resetAll = useCallback(() => {
        setEditing(false);
        resetFormFields();
    }, [resetFormFields]);

    useEffect(() => {
        if (isOpen) resetFormFields();
    }, [isOpen, resetFormFields]);

    const canSubmitForm = pixKey.trim().length > 0 && name.trim().length > 0 && !isSubmitting;
    const showCard = Boolean(savedKey && !editing);
    const showForm = editing || (!savedKey && canManage);
    const showNoKey = !savedKey && !canManage;

    const handleClose = () => {
        if (isSubmitting) return;
        resetAll();
        onClose();
    };

    const runSubmit = async (action: () => Promise<void>) => {
        setIsSubmitting(true);
        try {
            await action();
            resetAll();
        } catch {
            // erro já notificado pela camada de envio — mantém formulário aberto
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendSaved = () => {
        if (!savedKey) return;
        void runSubmit(() => onSubmit(toChatInput(savedKey.pixKey, savedKey.pixType, savedKey.pixName)));
    };

    const handleSaveAndSend = (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmitForm) return;
        const input = toChatInput(pixKey, type, name);
        if (!savedKey && !onSaveAndSend) return;
        void runSubmit(async () => {
            if (onSaveAndSend) await onSaveAndSend(input);
            else if (savedKey) await onSubmit(input);
        });
    };

    // Modal de envio (chave salva): 400×242 — formulário de edição fica em size padrão
    const sendShellClass = !showForm
        ? '!w-[400px] !max-w-[400px] !h-[242px] !max-h-[242px]'
        : undefined;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size="sm"
            className={sendShellClass}
        >
            {loading ? (
                <>
                    <Modal.Header onClose={handleClose} className="px-4 py-3 shrink-0">
                        Enviar chave Pix
                    </Modal.Header>
                    <Modal.Body className="px-4 py-3">
                        <p className="text-secondary">Carregando…</p>
                    </Modal.Body>
                </>
            ) : showForm ? (
                <ChatPixEditForm
                    savedKey={savedKey}
                    type={type}
                    pixKey={pixKey}
                    name={name}
                    isSubmitting={isSubmitting}
                    canSubmit={canSubmitForm}
                    hasSaveAndSend={Boolean(onSaveAndSend)}
                    onTypeChange={setType}
                    onPixKeyChange={setPixKey}
                    onNameChange={setName}
                    onClose={handleClose}
                    onSubmit={handleSaveAndSend}
                />
            ) : (
                <>
                    <Modal.Header onClose={handleClose} className="px-4 py-3 shrink-0">
                        Enviar chave Pix
                    </Modal.Header>
                    <Modal.Body className="px-4 py-2.5 !space-y-0 overflow-hidden">
                        {showCard && savedKey && (
                            <ChatPixSavedCard savedKey={savedKey} />
                        )}
                        {showNoKey && (
                            <p className="text-secondary">
                                Nenhuma chave PIX cadastrada. Peça a um admin.
                            </p>
                        )}
                    </Modal.Body>
                    {showCard && savedKey && (
                        <Modal.Footer className="px-4 py-3 shrink-0 bg-card">
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-primary shadow-none"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </Button>
                            {canManage && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        resetFormFields();
                                        setEditing(true);
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Editar
                                </Button>
                            )}
                            <Button
                                type="button"
                                size="sm"
                                onClick={handleSendSaved}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Enviando...' : 'Enviar'}
                            </Button>
                        </Modal.Footer>
                    )}
                    {showNoKey && (
                        <Modal.Footer className="px-4 py-3 shrink-0">
                            <Button type="button" size="sm" onClick={handleClose}>
                                Fechar
                            </Button>
                        </Modal.Footer>
                    )}
                </>
            )}
        </Modal>
    );
};

export default ChatPixForm;
